// Angular Core Imports
import { Component, computed, inject, input, OnInit } from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';

// Third-Party Imports
import { AccordionModule } from 'primeng/accordion';
import { AvatarModule } from 'primeng/avatar';
import { PanelModule } from 'primeng/panel';

// Application-Specific Imports
import { Consumer, ConsumerSpendWindow } from '@app/resources/services';
import { LoyaltyProgramsTypesEnum } from '@app/@shared/enums';
import { PortalTheme } from '@app/@shared/models';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';
import { formatVisitFrequency } from '@shared/utils';

interface SpendWindow {
  label: string;
  isLifetime: boolean;
  hasData: boolean;
  isOpen: boolean;
  totalSpend: number;
  visits: number;
  averageSpend: number;
  visitFrequency: string;
  rewardsEarned: number;
  pointsEarned: number;
}

@Component({
  selector: 'app-existing-member-information',
  standalone: true,
  imports: [AccordionModule, AvatarModule, DatePipe, CurrencyPipe, DecimalPipe, NgTemplateOutlet, PanelModule],
  templateUrl: './existing-member-information.component.html',
  styleUrl: './existing-member-information.component.scss',
})
export class ExistingMemberInformationComponent implements OnInit {
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly cssValidatorService = inject(CssValidatorService);

  member = input.required<Consumer>();

  public rewardBalance = 0;
  public assignDateTime = 0;
  public tenureDays = 0;

  protected theme: PortalTheme | undefined;

  public isPointsMerchant = false;
  public currentPointsBalance = 0;
  public lifetimeWindow: SpendWindow | null = null;
  public rollingWindows: SpendWindow[] = [];

  protected recentOrderPayment = computed(() => {
    const orderPayments = [...(this.member().OrderPayments ?? [])].sort((op1, op2) => {
      const date1 = new Date(op1.StartDateTime);
      const date2 = new Date(op2.StartDateTime);
      return date2.getTime() - date1.getTime();
    });
    return orderPayments.length > 0 ? orderPayments[0] : null;
  });

  ngOnInit(): void {
    const loyaltyProgram = this.member().LoyaltyProgram;
    const relationship = this.member().ConsumerRelationship;

    this.rewardBalance = loyaltyProgram?.RewardBalance ?? 0;
    this.currentPointsBalance = loyaltyProgram?.PointsBalance ?? 0;
    this.assignDateTime = relationship?.AssignDateTime ?? 0;
    this.tenureDays = relationship?.TenureDays ?? 0;

    const windows = (relationship?.SpendWindows ?? []).map((window) => this.toSpendWindow(window));
    this.lifetimeWindow = windows.find((window) => window.isLifetime) ?? null;
    this.rollingWindows = windows.filter((window) => !window.isLifetime);

    const applicationProfile = this.applicationProfileService.getApplicationProfile();

    const loyaltyProgramType = applicationProfile?.Merchant?.LoyaltyProgram?.LoyaltyProgramType;
    this.isPointsMerchant = (loyaltyProgramType & LoyaltyProgramsTypesEnum.Points) === LoyaltyProgramsTypesEnum.Points;

    this.theme = applicationProfile?.PortalTheme?.Theme;
    this.setCSSCustomProperties();
  }

  private setCSSCustomProperties(): void {
    if (this.theme) {
      document.documentElement.style.setProperty('--primary', this.theme.primary);
      document.documentElement.style.setProperty('--header-bg-primary', this.theme.header?.backgroundColor);
      document.documentElement.style.setProperty('--text--color-default', this.theme.defaultTextColor);

      this.cssValidatorService.checkUndefinedCssVariables('ExistingMemberInformationComponent');
    }
  }

  private toSpendWindow(window: ConsumerSpendWindow): SpendWindow {
    return {
      label: window.Days ? `Last ${window.Days} Days` : 'Lifetime',
      isLifetime: !window.Days,
      hasData: window.Visits > 0,
      isOpen: false,
      totalSpend: window.TotalSpend,
      visits: window.Visits,
      averageSpend: window.AverageSpend,
      visitFrequency: formatVisitFrequency(window.AverageDaysBetweenVisits),
      rewardsEarned: window.RewardsEarned,
      pointsEarned: window.PointsEarned,
    };
  }
}
